<template>
  <div class="work-order-form">
      <form @submit.stop.prevent="form.handleSubmit()">
        <!-- projects and workorder id -->
        <div class="grid grid-cols-2 gap-4">
          <form.Field
            name="projectId"
            :validators="{
              onBlur: yup.string().required('Project is required'),
            }"
          >
            <template #default="{ field }">
              <BaseSelect
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Project"
                :options="projects"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange((e.target as HTMLInputElement).value)"
              />
            </template>
          </form.Field>

          <form.Field
            name="workOrderId"
            :validators="{
              onBlur: yup.string().required('Work Order Number is required'),
            }"
          >
            <template #default="{ field }">
              <BaseInput
                :model-value="(field.state.value as string)"
                :name="field.name"
                type="text"
                label="Work Order #"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange((e.target as HTMLInputElement).value)"
              />
            </template>
          </form.Field>
        </div>

        <!-- subcontractor and wo date -->
        <div class="grid grid-cols-2 gap-4 mt-4">
          <form.Field
            name="subId"
            :validators="{
              onBlur: yup.string().required('Subcontractor is required'),
            }"
          >
            <template #default="{ field }">
              <BaseSelect
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Subcontractor"
                :options="subcontractors"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange((e.target as HTMLInputElement).value)"
              />
            </template>
          </form.Field>

          <form.Field
            name="date"
            :validators="{
              onBlur: yup.string().required('Date is required'),
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Work Order Date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange((e.target as HTMLInputElement).value)"
              />
            </template>
          </form.Field>
        </div>



        <h4 class="mt-6">Items</h4>
        <hr class="mt-2">

        <div v-if="lineItemsLoading || workOrdersLoading" class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
          <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
          <p class="text-sm text-center text-slate-500 pt-4">
            Loading...
          </p>
        </div>

        <!-- items -->
        <div v-show="!lineItemsLoading && !workOrdersLoading">
          <form.Field
            name="items"
            :validators="{
              onChange: yup.array().min(1, 'At least one item is required')
            }"
          >
            <template #default="{ field, state }">
              <div
                v-if="(state.meta.errors && state.meta.errors[0]) && form.getFieldValue('items').length === 0"
                class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
              >
                <span class="i-heroicons-exclaimation-circle size-8" />
                <p class="text-base/6 text-error sm:text-sm/6">
                  {{ state.meta.errors[0] }}
                </p>
              </div>

              <div
                v-for="(_, i) of state.value"
                :key="i"
                :name="`items[${i}]`"
              >
                <div class="flex justify-end mt-8">
                  <span
                    class="i-heroicons-x-circle cursor-pointer hover:text-error h-[1.2rem] w-[1.2rem]"
                    @click="removeItem(field, Number(i))"
                  />
                </div>

                <!-- Title and Cost -->
                <div class="grid grid-cols-2 gap-4">
                  <form.Field
                    :key="`title-${i}`"
                    :name="( `items[${i}].name` as any)"
                    :validators="{
                      onBlur: yup.string().required('Title is required'),
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        :model-value="(subField.state.value as string)"
                        type="text"
                        label="Title"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => subField.handleChange((e.target as HTMLInputElement).value)"
                      />
                    </template>
                  </form.Field>

                  <form.Field
                    :key="`cost-${i}`"
                    :name="(`items[${i}].cost` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Price must be a valid number',(value) => !isNaN(Number(value))).required('Price is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInputCurrency
                        :model-value="(subField.state.value as string)"
                        label="Price"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange((e.target as HTMLInputElement).value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <!-- Scope of Work -->
                <div class="mt-4">
                  <form.Field
                    :key="`scopeOfWork-${i}`"
                    :name="(`items[${i}].scopeOfWork` as any)"
                  >
                    <template #default="{ field: subField }">
                      <label class="select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium">Scope of Work</label>

                      <div class="mt-2">
                        <Editor
                          :model-value="(subField.state.value as string)"
                          @blur="subField.handleBlur"
                          @update:model-value="(value) => subField.handleChange(value)"
                        />
                      </div>
                    </template>
                  </form.Field>
                </div>
              </div>

              <div class="my-5">
                <BaseButton
                  variant="secondary"
                  type="button"
                  text="Add item"
                  :disabled="!isProjectSelected"
                  :class="!isProjectSelected ? 'opacity-50' : ''"
                  @click="addItem(field)"
                />
              </div>
            </template>
          </form.Field>
        </div>

        <div
          v-if="isProjectSelected"
          class="mt-5 w-fit ml-auto"
        >
          <div class="flex items-baseline justify-end bg-slate-100 px-4 pt-4 rounded-lg gap-4">
            <span class="text-slate-700">Total</span>
            <form.Field
              name="total"
              :validators="{
                onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Total must be a valid number',(value) => !isNaN(Number(value))).required('Total is required')
              }"
            >
              <template #default="{ field }">
                <BaseInputCurrency
                  :model-value="(field.state.value as string)"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  @blur="field.handleBlur"
                  @input="(e) => field.handleChange((e.target as HTMLInputElement).value)"
                />
              </template>
            </form.Field>
          </div>
        </div>

        <hr class="mt-6">

        <div class="flex justify-end gap-2 mt-3">
          <form.Subscribe>
            <template #default="{ canSubmit, isSubmitting }">
              <BaseButton
                variant="base"
                text="Cancel"
                :to="backTo ? { path: backTo } : { name: 'ListWorkOrders' }"
                :disabled="isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
              />

              <BaseButton
                variant="primary"
                text="Save"
                type="submit"
                :disabled="!canSubmit || isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
              />
            </template>
          </form.Subscribe>
        </div>
      </form>

  </div>
</template>
<script lang="ts" setup>
import { WorkOrder, WorkOrderItem, generateWorkOrderId } from '@/models/workOrder'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import Editor from '@/components/TextEditor.vue'
import { BudgetItem } from '@/models/budget'
import { extend, flatten, difference } from 'underscore'
import { PROJECT_STATUS } from '@/models/project'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { computed, onMounted, PropType, Ref, ref, watch } from 'vue'
import BudgetService from '@/services/BudgetService'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useAuth } from '@/composables/useAuth'
import { useNotification } from '@kyvg/vue3-notification'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import { decimalFormat, toNumber } from '@/utils'
import { useRoute } from 'vue-router'

type selectOption = {
  label: string
  value: string,
  disabled?: boolean
  [key: string]: any
}

const props = defineProps({
  projectId: {
    type: String,
    default: ''
  },
  projects: {
    type: Array as PropType<selectOption[]>,
    default: () => []
  },
  subcontractors: {
    type: Array as PropType<selectOption[]>,
    default: () => []
  },
  workOrder: {
    type: Object as PropType<WorkOrder>,
    default: null
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const workOrderFormData: Ref<WorkOrder> = ref(new WorkOrder())
workOrderFormData.value.projectId = props.projectId

const { currentUser } = useAuth()
const { notify } = useNotification()
const queryClient = useQueryClient()
const backTo = useRoute().query.backTo as string || ''

const form = useForm({
  defaultValues: props.workOrder ? props.workOrder : workOrderFormData.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const workOrder = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as WorkOrder
    workOrder.items = workOrder.items.map((item) => {
      const woItem = { ...item, cost: toNumber(item.cost) }
      return Object.fromEntries(Object.entries(woItem).filter(([, v]) => v !== undefined)) as unknown as WorkOrderItem
    })
    workOrder.date = new Date(workOrder.date)
    workOrder.total = toNumber(workOrder.total)
    workOrder.submittedBy = currentUser.value.id
    workOrder.projectId = workOrder.budgetId
    await props.saveFunction(workOrder)
    queryClient.invalidateQueries({ queryKey: ['WorkOrders', selectedProjectId.value] })
  },
})

const budgetService = new BudgetService()
const selectedProjectId = form.useStore((state) => state.values.projectId)
const isProjectSelected = computed(() =>  selectedProjectId.value !== '')
const enableWOQuery = computed(() => budgetLineItems.value?.length > 0)

const setTotal = () => {
  const total = form.getFieldValue('items').reduce((acc, item) => {
    return acc + toNumber(item.cost)
  }, 0)

  if (total) {
    form.setFieldValue('total', decimalFormat(total, 2) as any)
  } else {
    form.setFieldValue('total', String(0) as any)
  }
}

const { data: budgetLineItems, isLoading: lineItemsLoading } = useQuery({
  queryKey: ['LineItems', selectedProjectId],
  queryFn: () => budgetService.getLineItems(selectedProjectId.value),
  enabled: isProjectSelected
})

const { data: otherWorkOrders, isLoading: workOrdersLoading } = useQuery({
  queryKey: ['WorkOrders', selectedProjectId],
  queryFn: async () => {
    const workOrders = await budgetService.getWorkOrders(selectedProjectId.value)
    return workOrders
  },
  enabled: enableWOQuery
})


watch(() => [selectedProjectId.value, isProjectSelected.value, workOrdersLoading.value, lineItemsLoading.value], () => {
  const allLoaded = selectedProjectId.value && isProjectSelected.value && !workOrdersLoading.value && !lineItemsLoading.value
  if (allLoaded && otherWorkOrders.value && budgetLineItems.value) {
    projectSelected(selectedProjectId.value, budgetLineItems.value, otherWorkOrders.value)
  }
}, { immediate: true })

const projectSelected = async (projectId, lineItems: BudgetItem[], workOrders: WorkOrder[]) => {
  const selectedProject = props.projects.find((project) => project.value === projectId)

  if (selectedProject.value && selectedProject.status === PROJECT_STATUS.IN_PROGRESS) {
    try {
      form.setFieldValue('budgetId', selectedProject.value)

      const itemsOnExistingWorkOrder = flatten(workOrders.map((workOrder) => { return workOrder.lineItems }))
      const workOrderItems = []
      form.setFieldValue('lineItems', [])

      lineItems.forEach((item: BudgetItem) => {
        if (item.selected && !item.isCreditLineItem) {
          if (itemsOnExistingWorkOrder.indexOf(item.id) === -1) {
            const workOrderItem = extend(new WorkOrderItem(), {
              name: item.title,
              scopeOfWork: item.scopeOfWork,
              cost: String(item.laborBudget),
              lineItems: [item.id]
            })
            workOrderItems.push(workOrderItem)

            const itemIdNotinArray = form.getFieldValue('lineItems').indexOf(item.id) === -1
            if (itemIdNotinArray) {
              form.pushFieldValue('lineItems', item.id)
            }
          }
        }
      })

      form.setFieldValue('items', workOrderItems)
      form.validateField('items', 'change')

      setTotal()
    } catch (err) {
      notify({
        type: 'error',
        title: 'Error',
        text: err.message || 'Error getting project budget'
      })
      console.log(err)
    }
  } else {
    // @todo display error modal
  }
}

const addItem = (field) => {
  field.pushValue({
    ...new WorkOrderItem(),
    cost: '0' as any
  } as WorkOrderItem)
}

const removeItem = (field, index) => {
  const woItem = { ...form.getFieldValue('items')[index] }
  const woItemlineItems = woItem.lineItems

  if (woItemlineItems.length) {
    form.setFieldValue('lineItems', difference(form.getFieldValue('lineItems'), woItemlineItems))
  }
  field.removeValue(index)
  setTotal()
}

onMounted( async () => {
  if (form.getFieldValue('workOrderId') === '') {
    const workOrderId = await generateWorkOrderId()
    form.setFieldValue('workOrderId', workOrderId)
    form.validateField('workOrderId', 'blur')
  }
})
</script>
